import { useEffect, useState, useRef } from "react";
import "./CategoriesTree.css";
import "./CategoriesTree.responsive.css";
import Modal from "../Modal/Modal";
import api from "../../api";
import { toast } from "react-toastify";
import ConfirmModal from "../Modal/ConfirmModal";
import Button from "@mui/material/Button";
import PageHeader from "../PageHeader/PageHeader";
import RowActions from "../DataTable/RowActions";
import TablePagination from "../DataTable/TablePagination";
import useCachedFetch from "../../hooks/useCachedFetch";
import useBreakpoint from "../../hooks/useBreakpoint";

const PAGE_SIZE = 15;

const SkeletonCard = () => (
  <div className="skeleton-card">
    <div className="skeleton-card-header">
      <div className="skeleton-card-left">
        <div className="skeleton sk-icon" />
        <div className="skeleton sk-icon" />
        <div className="skeleton sk-title" />
      </div>
      <div className="skeleton-card-right">
        <div className="skeleton skeleton-badge" />
        <div className="skeleton skeleton-badge" />
        <div className="skeleton-card-actions">
          <div className="skeleton skeleton-btn" />
          <div className="skeleton skeleton-btn" />
          <div className="skeleton skeleton-btn" />
        </div>
      </div>
    </div>
  </div>
);

const CategoriesTree = () => {
  // Below 993px the row buttons collapse into a ⋮ menu
  const compact = useBreakpoint() !== "desktop";

  const [openCategory, setOpenCategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditSubModal, setShowEditSubModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editing, setEditing] = useState({ id: null, title: "", name: "" });

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "delete",
  });

  const [filters, setFilters] = useState({ sort: "default" });

  const {
    data,
    total,
    loading,
    hasMore,
    page,
    fetch,
    refetch,
    invalidate,
    nextPage,
    prevPage,
  } = useCachedFetch({
    cacheKey: "categories",
    endpoint: "/categories/get-all",
    params: filters,
    limit: PAGE_SIZE,
  });

  const fetchRef = useRef(fetch);

  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  // Fetch on filter change
  useEffect(() => {
    fetchRef.current(0);
  }, [filters]);

  /* ── CONFIRM MODAL ── */
  const confirmDelete = (title, message, onConfirm, type = "delete") => {
    setConfirmModal({ open: true, title, message, onConfirm, type });
  };

  const closeConfirm = () => {
    setConfirmModal({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "delete",
    });
  };

  /* ── CRUD ── */
  const updateCategory = async (id, updatedName) => {
    await api.put(`/categories/update/${id}`, { name: updatedName });
    toast.success("Updated successfully!");
    invalidate();
    refetch();
  };

  const addCategory = async (categoryName) => {
    if (!categoryName) return;
    await api.post("/categories/create", { name: categoryName });
    toast.success("Created successfully!");
    invalidate();
    refetch();
  };

  const deleteCategory = async (id) => {
    await api.delete(`/categories/delete/${id}`);
    toast.success("Deleted successfully!");
    invalidate();
    refetch();
  };

  const addSubCategory = async (catId, title) => {
    await api.post("/sub-categories/create-sub", { categoryId: catId, title });
    toast.success("Created successfully!");
    invalidate();
    refetch();
  };

  const updateSub = async (catId, subId, updatedName) => {
    await api.post("/sub-categories/update-sub", {
      categoryId: catId,
      subCategoryId: subId,
      name: updatedName,
    });
    toast.success("Updated successfully!");
    invalidate();
    refetch();
  };

  const deleteSub = async (catId, subId) => {
    await api.post("/sub-categories/delete-sub", {
      categoryId: catId,
      subCategoryId: subId,
    });
    toast.success("Deleted successfully!");
    invalidate();
    refetch();
  };

  const addSkill = async (categoryId, subCategoryId, skillName) => {
    await api.post("/skills/add-skill", {
      categoryId,
      subCategoryId,
      name: skillName,
    });
    toast.success("Skill added!");
    invalidate();
    refetch();
  };

  const deleteSkill = async (categoryId, subCategoryId, skillId) => {
    await api.post("/skills/delete-skill", {
      categoryId,
      subCategoryId,
      skillId,
    });
    toast.success("Skill removed!");
    invalidate();
    refetch();
  };

  /* ── ROW ACTIONS ── */
  const categoryActions = (cat) => [
    {
      key: "add-sub",
      label: "Add Sub-Category",
      icon: "add",
      color: "primary",
      onClick: () => {
        setSelectedCategoryId(cat.id);
        setShowSubModal(true);
      },
    },
    {
      key: "edit",
      label: "Edit",
      icon: "edit",
      color: "neutral",
      onClick: () => {
        setEditing({ id: cat.id, title: "Edit Category", name: cat.name });
        setSelectedCategoryId(cat.id);
        setShowEditModal(true);
      },
    },
    {
      key: "delete",
      label: "Delete",
      icon: "delete",
      color: "error",
      onClick: () =>
        confirmDelete(
          "Delete Category",
          `Are you sure you want to delete "${cat.name}"?`,
          () => {
            deleteCategory(cat.id);
            closeConfirm();
          },
          "permanent",
        ),
    },
  ];

  const subCategoryActions = (cat, sub) => [
    {
      key: "edit",
      label: "Edit",
      icon: "edit",
      color: "neutral",
      onClick: () => {
        setSelectedCategoryId(cat.id);
        setSelectedSubCategoryId(sub.id);
        setEditing({ id: sub.id, title: "Edit Sub-Category", name: sub.name });
        setShowEditSubModal(true);
      },
    },
    {
      key: "delete",
      label: "Delete",
      icon: "delete",
      color: "error",
      onClick: () =>
        confirmDelete(
          "Delete Sub-Category",
          `Are you sure you want to delete "${sub.name}"?`,
          () => {
            deleteSub(cat.id, sub.id);
            closeConfirm();
          },
          "permanent",
        ),
    },
  ];

  return (
    <div className="tree-wrapper">
      <PageHeader
        title="Categories Management"
        description="Manage top level categories and skill groupings here."
        actions={
          <>
            <div className="select-wrapper">
              <select
                aria-label="Sort categories"
                value={filters.sort}
                onChange={(e) => setFilters({ sort: e.target.value })}
              >
                <option value="default">Default Order</option>
                <option value="a-z">Name (A → Z)</option>
                <option value="z-a">Name (Z → A)</option>
              </select>
              <span className="material-symbols-outlined select-arrow">
                expand_more
              </span>
            </div>

            <Button
              size="medium"
              variant="contained"
              color="primary"
              startIcon={<span className="material-symbols-outlined">add</span>}
              onClick={() => setShowCategoryModal(true)}
            >
              Add Category
            </Button>
          </>
        }
      />

      <div className="table-wrapper">
        {/* COLUMN HEADERS (hidden on phones, where each category is a stacked card) */}
        <div className="header-row">
          <div>
            <div className="header-col col-details">
              CATEGORY / SUB-CATEGORY
            </div>
          </div>
          <div className="col-cat-actions">
            <div className="header-col col-posted-by">SKILLS</div>
            <div className="header-col col-posted-date">STATUS</div>
            <div className="header-col col-delivery-date">ACTIONS</div>
          </div>
        </div>

        {/* TREE */}
        <div className="tree-container">
          {loading ? (
            Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <SkeletonCard key={i} />
            ))
          ) : data.length === 0 ? (
            <div className="category-card">
              <div
                className="category-header"
                style={{ textAlign: "center", padding: "40px", color: "#aaa" }}
              >
                No categories found
              </div>
            </div>
          ) : (
            data?.map((cat) => (
              <div
                key={cat.id}
                className={`category-card ${openCategory === cat.id ? "open" : ""}`}
              >
                <div
                  className="category-header"
                  onClick={() =>
                    setOpenCategory(openCategory === cat.id ? null : cat.id)
                  }
                >
                  <div className="category-title">
                    <span
                      className={`material-symbols-outlined arrow ${openCategory === cat.id ? "open" : ""}`}
                    >
                      keyboard_arrow_right
                    </span>
                    <span className="material-symbols-outlined folder">
                      {openCategory === cat.id ? "folder_open" : "folder"}
                    </span>
                    {cat.name}
                  </div>
                  <div className="meta-container">
                    <div className="meta-info">
                      <span className="meta-item">
                        {cat.sub_categories?.reduce(
                          (acc, sub) => acc + (sub.skills?.length || 0),
                          0,
                        )}{" "}
                        Skills
                      </span>
                    </div>
                    <div className="meta-info">
                      <span
                        className={`status ${cat.sub_categories?.length > 0 ? "active" : "inactive"}`}
                      >
                        {cat.sub_categories?.length > 0 ? "Active" : "Empty"}
                      </span>
                    </div>
                    <RowActions
                      inline={categoryActions(cat)}
                      collapsed={compact}
                    />
                  </div>
                </div>

                {/* SUBCATEGORIES */}
                {openCategory === cat.id && (
                  <div className="sub-container expand">
                    {cat.sub_categories?.length === 0 ? (
                      <p
                        style={{
                          padding: "10px",
                          color: "#888",
                          fontSize: "14px",
                        }}
                      >
                        No sub-categories
                      </p>
                    ) : (
                      cat.sub_categories?.map((sub) => (
                        <div className="sub-card" key={sub.id || sub.name}>
                          <div className="sub-category-header">
                            <div className="sub-card-heading">
                              <span className="material-symbols-outlined">
                                folder
                              </span>
                              <strong>{sub.name}</strong>
                            </div>
                            <div className="sub-meta-container">
                              <div className="sub-meta">
                                {sub.skills?.length || 0} Skills
                              </div>
                              <RowActions
                                inline={subCategoryActions(cat, sub)}
                                collapsed={compact}
                              />
                            </div>
                          </div>

                          {/* SKILLS */}
                          <div className="skills">
                            {sub.skills?.length === 0 ? (
                              <p
                                style={{
                                  color: "#aaa",
                                  fontSize: "13px",
                                  padding: "4px 0",
                                }}
                              >
                                No skills
                              </p>
                            ) : (
                              sub.skills?.map((skill) => (
                                <div key={skill.id || skill} className="skill">
                                  {typeof skill === "string"
                                    ? skill
                                    : skill.name}
                                  <span
                                    className="material-symbols-outlined removeSkill"
                                    onClick={() =>
                                      confirmDelete(
                                        "Delete Skill",
                                        `Are you sure you want to delete "${skill.name}"?`,
                                        () => {
                                          deleteSkill(cat.id, sub.id, skill.id);
                                          closeConfirm();
                                        },
                                        "permanent",
                                      )
                                    }
                                  >
                                    close
                                  </span>
                                </div>
                              ))
                            )}
                            <Button
                              size="small"
                              variant="text"
                              color="important"
                              startIcon={
                                <span className="material-symbols-outlined">
                                  add
                                </span>
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCategoryId(cat.id);
                                setSelectedSubCategoryId(sub.id);
                                setShowSkillModal(true);
                              }}
                            >
                              Add Skill
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <TablePagination
          page={page}
          limit={PAGE_SIZE}
          total={total}
          count={data.length}
          hasMore={hasMore}
          loading={loading}
          onPrev={prevPage}
          onNext={nextPage}
          noun="categories"
        />
      </div>

      {/* MODALS */}
      <Modal
        open={showCategoryModal}
        title="Add New Category"
        initialValues={{}}
        fields={[{ name: "name", label: "Category Name", required: true }]}
        onClose={() => setShowCategoryModal(false)}
        onSubmit={(data) => {
          addCategory(data.name);
          setShowCategoryModal(false);
        }}
      />
      <Modal
        open={showSubModal}
        title="Add Sub-Category"
        initialValues={{}}
        fields={[{ name: "name", label: "Subcategory Name", required: true }]}
        onClose={() => setShowSubModal(false)}
        onSubmit={(data) => {
          addSubCategory(selectedCategoryId, data.name);
          setShowSubModal(false);
        }}
      />
      <Modal
        open={showSkillModal}
        title="Add Skill"
        initialValues={{}}
        fields={[{ name: "name", label: "Skill Name", required: true }]}
        onClose={() => setShowSkillModal(false)}
        onSubmit={(data) => {
          addSkill(selectedCategoryId, selectedSubCategoryId, data.name);
          setShowSkillModal(false);
        }}
      />
      <Modal
        open={showEditModal}
        title={editing.title}
        initialValues={{ name: editing.name }}
        fields={[
          {
            name: "name",
            label: "Category Name",
            required: true,
            value: editing.name,
          },
        ]}
        onClose={() => setShowEditModal(false)}
        onSubmit={(data) => {
          updateCategory(selectedCategoryId, data.name);
          setShowEditModal(false);
        }}
      />
      <Modal
        open={showEditSubModal}
        title={editing.title}
        initialValues={{ name: editing.name }}
        fields={[
          {
            name: "name",
            label: "Sub-Category Name",
            required: true,
            value: editing.name,
          },
        ]}
        onClose={() => setShowEditSubModal(false)}
        onSubmit={(data) => {
          updateSub(selectedCategoryId, selectedSubCategoryId, data.name);
          setShowEditSubModal(false);
        }}
      />
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        type={confirmModal.type}
      />
    </div>
  );
};

export default CategoriesTree;
